import { useState } from "react";
import { Search, Trash2, Phone, Mail, PhoneCall, X } from "lucide-react";
import ConfirmDeleteDrawer from "../ConfirmDeleteDrawer";
import { calcularEstadoFromLead, ESTADO_LABELS, ESTADO_COLORS, ESTADO_ROW_COLORS } from "../../lib/leadEstado";
import { LeadSourcePill } from "./LeadSourceBadge";

export default function LeadsList({ leads, onEdit, onDelete, filters, setFilters, canalOptions = [], servicioOptions = [] }) {
  const [search, setSearch] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);

  const leadsWithEstado = leads.map((lead) => ({
    ...lead,
    estadoCalculado: calcularEstadoFromLead(lead),
  }));

  const filteredLeads = leadsWithEstado.filter((lead) => {
    if (search) {
      const s = search.toLowerCase();
      if (!lead.nombre?.toLowerCase().includes(s) && !lead.telefono?.includes(s) && !lead.email?.toLowerCase().includes(s)) {
        return false;
      }
    }
    if (filters.estado && lead.estadoCalculado !== filters.estado) return false;
    if (filters.canal && lead.canal !== filters.canal) return false;
    if (filters.servicio && lead.servicio !== filters.servicio) return false;
    if (filters.mes) {
      const dateStr = filters.mesBy === "resultado"
        ? lead.fecha_resultado
        : lead.fecha_contacto;
      if (!dateStr) return false;
      const leadMonth = new Date(dateStr).getMonth() + 1;
      if (leadMonth !== parseInt(filters.mes)) return false;
    }
    return true;
  });

  const clearFilters = () => {
    setFilters({ mes: "", mesBy: "contacto", estado: "", canal: "", servicio: "" });
    setSearch("");
  };

  const hasFilters = filters.estado || filters.canal || filters.servicio || filters.mes || search;

  return (
    <div>
      {/* Search & Filters */}
      <div className="mb-4 space-y-3">
        <div className="relative">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, teléfono o email..."
            className="w-full pl-10 pr-4 py-2.5 text-sm border border-gray-200 rounded-lg focus:ring-2 focus:ring-brand/20 focus:border-brand outline-none"
          />
        </div>

        <div className="flex items-center gap-2 text-xs">
          <span className="text-gray-500">Filtrar mes por:</span>
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-0.5">
            {[
              { v: "contacto", label: "Contacto" },
              { v: "resultado", label: "Resultado" },
            ].map((opt) => (
              <button
                key={opt.v}
                onClick={() => setFilters({ ...filters, mesBy: opt.v })}
                className={`px-2.5 py-1 rounded-md transition-colors ${
                  filters.mesBy === opt.v
                    ? "bg-brand text-white"
                    : "text-gray-600 hover:text-gray-900"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <select
            value={filters.mes}
            onChange={(e) => setFilters({ ...filters, mes: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="">Todos los meses</option>
            {["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"].map((m, i) => (
              <option key={i} value={i + 1}>{m}</option>
            ))}
          </select>
          <select
            value={filters.estado}
            onChange={(e) => setFilters({ ...filters, estado: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
          <select
            value={filters.canal}
            onChange={(e) => setFilters({ ...filters, canal: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="">Todos los canales</option>
            {canalOptions.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
          </select>
          <select
            value={filters.servicio}
            onChange={(e) => setFilters({ ...filters, servicio: e.target.value })}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white"
          >
            <option value="">Todos los servicios</option>
            {servicioOptions.map((o) => <option key={o.id} value={o.name}>{o.name}</option>)}
          </select>
        </div>

        {hasFilters && (
          <button onClick={clearFilters} className="text-xs text-brand hover:underline flex items-center gap-1">
            <X size={14} /> Limpiar filtros
          </button>
        )}
      </div>

      {/* Results count */}
      <p className="text-xs text-gray-500 mb-3">{filteredLeads.length} lead(s) encontrado(s)</p>

      {/* List */}
      {filteredLeads.length > 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden divide-y divide-gray-100">
          {filteredLeads.map((lead) => {
            const llamadas = lead.llamadas || [];
            const totalLlamadas = llamadas.length;
            const realizadas = llamadas.filter(l => l.realizada).length;
            const rowTint = ESTADO_ROW_COLORS[lead.estadoCalculado] || ESTADO_ROW_COLORS.pendiente;

            return (
              <div
                key={lead.id}
                onClick={() => onEdit(lead)}
                className={`p-4 transition-colors cursor-pointer ${rowTint}`}
              >
                <div className="flex items-start gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-gray-900 truncate">{lead.nombre}</span>
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${ESTADO_COLORS[lead.estadoCalculado] || ESTADO_COLORS.pendiente}`}>
                        {ESTADO_LABELS[lead.estadoCalculado] || "Pendiente"}
                      </span>
                      <LeadSourcePill source={lead.source} />
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span>Contacto: {new Date(lead.fecha_contacto).toLocaleDateString("es-ES")}</span>
                      {lead.estadoCalculado === "convertido" && lead.fecha_resultado && (
                        <span className="text-emerald-600">
                          Alta: {new Date(lead.fecha_resultado).toLocaleDateString("es-ES")}
                        </span>
                      )}
                      {lead.canal && <span>{lead.canal}</span>}
                      {lead.servicio && <span>{lead.servicio}</span>}
                      {lead.sesion_prueba && <span className="text-emerald-600">Sesión prueba</span>}
                    </div>
                    {(lead.telefono || lead.email) && (
                      <div className="flex gap-3 mt-2 text-xs text-gray-500">
                        {lead.telefono && <span className="flex items-center gap-1"><Phone size={12} />{lead.telefono}</span>}
                        {lead.email && <span className="flex items-center gap-1"><Mail size={12} />{lead.email}</span>}
                      </div>
                    )}
                    {(totalLlamadas > 0 || lead.observaciones) && (
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-xs">
                        {totalLlamadas > 0 && (
                          <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-medium ${
                            realizadas === totalLlamadas ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"
                          }`}>
                            <PhoneCall size={12} />
                            {realizadas}/{totalLlamadas}
                          </span>
                        )}
                        {lead.observaciones && (
                          <span className="text-gray-400 truncate max-w-[200px] sm:max-w-xs">
                            {lead.observaciones}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); setDeleteTarget(lead); }}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg shrink-0"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          <p>No se encontraron leads</p>
          <p className="text-sm mt-1">Ajusta los filtros o crea un nuevo lead</p>
        </div>
      )}

      {deleteTarget && (
        <ConfirmDeleteDrawer
          title="Eliminar lead"
          itemName={deleteTarget.nombre}
          message="¿Estás seguro de que quieres eliminar este lead?"
          warningText="Esta acción no se puede deshacer."
          onConfirm={() => { onDelete(deleteTarget.id); setDeleteTarget(null); }}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
}
